import { useEffect, useState } from "react";
import { BellRing, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const urlB64ToUint8 = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export function PushToggle() {
  const supported = typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((s) => setSubscribed(!!s))
      .catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permesso notifiche negato dal dispositivo"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid_public_key");
      if (!data.key) { toast.error("Notifiche push non configurate sul server"); return; }
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.key) });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setSubscribed(true);
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (e) {
      toast.error("Impossibile attivare le notifiche push");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); await sub.unsubscribe(); }
      setSubscribed(false);
      toast.success("Notifiche push disattivate su questo dispositivo");
    } catch { toast.error("Errore durante la disattivazione"); } finally { setBusy(false); }
  };

  if (!supported) return null;

  return (
    <button
      data-testid="push-toggle"
      onClick={subscribed ? disable : enable}
      disabled={busy}
      title={subscribed ? "Notifiche push attive su questo dispositivo — clicca per disattivare" : "Attiva le notifiche push su questo dispositivo"}
      aria-label={subscribed ? "Disattiva notifiche push" : "Attiva notifiche push"}
      className={`w-10 h-10 flex items-center justify-center rounded-sm border transition-colors duration-200 ${
        subscribed ? "border-neon-green/50 text-neon-green bg-neon-green/10" : "border-white/15 text-white hover:border-neon-green hover:text-neon-green"
      }`}
    >
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : subscribed ? <BellRing className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
    </button>
  );
}
